/**
 * THE SEASON BUILD — lifted out of ResearchStudio.tsx so it can be RUN, not
 * just read.
 *
 * Chris, 2026-08-25: "I fix one thing, you break another and the cycle is
 * almost infinite." The reason that kept happening is that the thing which
 * actually produces his answer — Best fit, month by month, colour by colour —
 * lived inside a 3,600-line React component, tangled with state, effects and
 * fetches. It could only be exercised by a human clicking a button, so every
 * defect in it was found by him, live, after it had already produced a wrong
 * number. March 2026 was never modelled AT ALL for three sessions because of a
 * hardcoded probe date, and nothing could catch that but him.
 *
 * Everything here is PURE and SYNCHRONOUS. No React, no fetch, no clock, no
 * randomness. Give it a month's calendar, its demand and its real working
 * timetable and it returns the built timetables, both sides of the money, and
 * a log of exactly what it did and did not do. `.studiotest/seasonbuild.test.ts`
 * runs the WHOLE nine-month season through it against the real database exports
 * in about a second.
 *
 * The component's job is now only what a component should do: fetch, await,
 * save, render.
 *
 *   ResearchStudio.designSeason()
 *     for each month:
 *       ensureMonthWtt(mk)        <- I/O, stays in the component
 *       groupRunningDates(...)    <- studio/season.ts (pure, gated)
 *       buildMonth(...)           <- HERE (pure, gated)
 *       saveSlice(...)            <- I/O, stays in the component
 *
 * Chris's laws that live in this file (93 section 3):
 *   1. Marginal vs day-fixed — per-working decisions use `.marginal` (inside
 *      bestFit); the P&L uses `.total`.
 *   3. NR paths are physics — the path budget is the colour's OBSERVED usage and
 *      is never relaxed.
 *   4. Steam is the product — the fleet overflow rule designates DIESEL, it
 *      never invents a steam engine, and it never changes steam haulage
 *      elsewhere. Locomotives may not appear or vanish (2026-09-07): every
 *      engine starts and ends its day at Grosmont or Pickering, and one that
 *      would not is a point where the Brief bound (kind 'loco').
 *   6. Every month and day on its OWN data — the design is per colour (the unit
 *      the railway publishes), the VALUATION is per day-type.
 *   8. No garbage — every skip is logged with its reason, and an empty result is
 *      returned as an empty result, never as a fallback wearing a design badge.
 */
import type {
  CarriageSet, ClassDemand, CostModel, Departure, EngineParams, FleetCaps,
  Network, SetFormation, Slot, StudioMode,
} from './types'
import { buildNetwork, observedNrTraversals, withPathBudget } from './network'
import { oneWayShares, runClasses, stampOneWay } from './engine'
import { bestFit } from './bestfit'
import { ilpBuild } from './ilpEngine'
import { applySetPlan, planSets } from './stock'
import { crossingConflicts, pathUsage, stockImbalances } from './constraints'
import { costFromDeps, ecsCapOf } from './costs'
import { wttToDeps, type BaselineWorking } from './baseline'
import { toMin } from './demand'
// The season table owns this shape; the build fills it. Type-only import, so
// there is no runtime dependency from the model on the UI.
import type { YearSideSnap } from './YearComparePanel'
import {
  blendParts, coloursWithoutPattern, straddleWarning, unscorableColour,
  type CalendarDay, type DayClassSpan, type DemandPart, type SeasonDayGroup,
} from './season'

// ---------------------------------------------------------------------------
//  Shapes
// ---------------------------------------------------------------------------

/** One colour's designed departures, banked to every date it covers. */
export interface SeasonDayPlan extends SeasonDayGroup {
  deps: Departure[]
  dl?: number
  /** Who is one-way on TODAY's timetable, per day-type then per demand slot
   *  (engine.ts::oneWayShares) — banked with the plan so every per-date reader
   *  values the design against the same people the build did. */
  oneWayToday?: Record<string, Record<string, number>>
}

/** What a colour's plan is designed against, and what it is valued against. */
export interface GroupDemand {
  /** ONE representative day of the whole week — what Best fit builds for. */
  design: { cls: ClassDemand; un: number; basis: string }
  /** Every day-type in the group, on ITS OWN sales — what the money uses. */
  parts: DemandPart[]
  /** "Saturday x5 - Weekday x9" — for the screen. */
  mix: string
  days: number
  /** Per day-type, per slot: the share of its passengers who are one-way on
   *  TODAY's timetable (present when the caller supplied today's timetable). */
  oneWayToday?: Record<string, Record<string, number>>
}

/** What today's timetable is, for marking who travels one way on it. */
export interface TodayTimetable { net: Network; deps: Departure[]; params: EngineParams }

// ---------------------------------------------------------------------------
//  Where the Brief bound — ONE owner of "why is this month outside the Brief"
// ---------------------------------------------------------------------------

/** A change to the Brief that would settle an issue — applied by the planner,
 *  never by the build. */
export type MonthFixChange = Partial<{ maxUnreturned: number; maxSteam: number; maxDiesel: number; maxEcsDiesel: number; ecsDiesel: boolean }>
export interface MonthFix { label: string; change: MonthFixChange }
/**
 * One point where a designed month is outside the Brief, in words, with the
 * Brief changes that would settle it. Read by Results → Financial Impact, ⚠ Interventions, the
 * season table, the board paper, the month pills, the build log and the
 * end-of-build review panel — nowhere else computes this.
 */
export interface MonthIssue {
  /** 'loco' = an engine would have to appear from nowhere or vanish at a station
   *  with no shed (Chris, 2026-09-07) — no Brief change settles it. */
  kind: 'home' | 'fleet' | 'paths' | 'stock' | 'crossing' | 'loco'
  colour: string
  /** Passengers a day (home) or engines (fleet) — for headlines. */
  count: number
  text: string
  fixes: MonthFix[]
}

export function colourIssues(
  s: ColourScore,
  colour: string,
  env: Pick<BuildEnv, 'maxUnreturned' | 'fleet' | 'classLabels' | 'ecsDiesel'>,
): MonthIssue[] {
  const out: MonthIssue[] = []
  const allowance = env.maxUnreturned ?? 0
  const label = (dc: string) => env.classLabels?.[dc] ?? dc
  if (s.unreturned > Math.max(0.5, allowance)) {
    const worst = [...s.byClass].sort((a, b) => b.unreturned - a.unreturned)[0]
    const n = Math.round(s.unreturned)
    const step = Math.max(5, Math.ceil(n / 5) * 5)
    out.push({
      kind: 'home', colour, count: n,
      text: `${colour}: ${n} passengers a day without a seat home on ${worst ? label(worst.dayClass) : 'its busiest day'}${worst?.reason ? ` — ${worst.reason}` : ''}; the Scope allows ${allowance} a day`,
      fixes: [{ label: `Allow ${step} a day without a seat home`, change: { maxUnreturned: step } }],
    })
  }
  const need = s.cost.fleetNeed
  const maxSteam = env.fleet.maxSteam
  // Law 4: a design that wants more steam than the Brief holds runs the overflow
  // DIESEL — never a phantom steam engine, and the model never adds steam by
  // itself. Where that happens it is said against the Brief's fleet, so a
  // fourth steam engine is shown, never hidden (Chris, 2026-09-07).
  const steamOver = Math.max(0, (s.steamWanted ?? 0) - maxSteam)
  if (need.steam > maxSteam) {
    out.push({ kind: 'fleet', colour, count: need.steam, text: `${colour}: needs ${need.steam} steam locos; the Scope allows ${maxSteam}`, fixes: [{ label: `Allow ${need.steam} steam locos`, change: { maxSteam: need.steam } }] })
  }
  if (need.diesel > env.fleet.maxDiesel) {
    const standIn = steamOver > 0
      ? ` (${Math.min(steamOver, need.diesel)} of them standing in for steam: the timetable wants ${s.steamWanted} steam engines to keep every diagram inside footplate hours, and the Scope allows ${maxSteam})`
      : ''
    out.push({
      kind: 'fleet', colour, count: need.diesel,
      text: `${colour}: needs ${need.diesel} diesel locos on passenger trains${standIn}; the Scope allows ${env.fleet.maxDiesel}`,
      fixes: [
        { label: `Allow ${need.diesel} diesel locos for passenger trains`, change: { maxDiesel: need.diesel } },
        ...(steamOver > 0 ? [{ label: `Allow ${s.steamWanted} steam locos`, change: { maxSteam: s.steamWanted } }] : []),
      ],
    })
  }
  const ecsAllowed = ecsCapOf(env.fleet)
  if (need.ecs > ecsAllowed) {
    // In plain English (Chris, 2026-09-07): `need.ecs` is the engines the stock
    // moves need beyond any passenger diesel standing idle — what the Brief's
    // "ECS stock moves" slot would have to be raised to.
    const idle = Math.max(0, env.fleet.maxDiesel - need.diesel)
    out.push({
      kind: 'fleet', colour, count: need.ecs,
      text: `${colour}: the empty-stock moves need ${need.ecs} engine${need.ecs === 1 ? '' : 's'} of their own${idle > 0 ? ` on top of the ${idle} passenger diesel${idle === 1 ? '' : 's'} standing idle` : ''}; the Scope sets aside ${ecsAllowed} engine${ecsAllowed === 1 ? '' : 's'} for stock moves`,
      fixes: [
        { label: `Set aside ${need.ecs} engine${need.ecs === 1 ? '' : 's'} for empty-stock moves`, change: { maxEcsDiesel: need.ecs } },
        ...(env.ecsDiesel !== false ? [{ label: 'Let empty-stock moves between the sheds share a steam engine (ECS on diesel off)', change: { ecsDiesel: false } }] : []),
      ],
    })
  }
  if (need.railcar > env.fleet.maxRailcar) {
    out.push({ kind: 'fleet', colour, count: need.railcar, text: `${colour}: needs ${need.railcar} railcars; the Scope allows ${env.fleet.maxRailcar}`, fixes: [] })
  }
  // Locomotives may not appear or vanish (Chris, 2026-09-07): every engine
  // starts and ends its day at Grosmont or Pickering. One that would begin or
  // end at a station with no shed is a point where the Brief bound — no Brief
  // change settles it; the timetable itself needs a working added, retimed or
  // withdrawn. Never a silent pass.
  for (const lo of s.cost.locoOut ?? []) {
    out.push({ kind: 'loco', colour, count: 1, text: `${colour}: ${lo.text}`, fixes: [] })
  }
  if (s.pathsOver > 0) out.push({ kind: 'paths', colour, count: s.pathsOver, text: `${colour}: plans ${s.pathsOver} more Network Rail path${s.pathsOver === 1 ? '' : 's'} than the railway runs today — paths are physics; a working must come out`, fixes: [] })
  if (s.stockOut > 0) out.push({ kind: 'stock', colour, count: s.stockOut, text: `${colour}: ${s.stockOut} rake${s.stockOut === 1 ? '' : 's'} left out of place overnight`, fixes: [] })
  if (s.crossingsBad > 0) out.push({ kind: 'crossing', colour, count: s.crossingsBad, text: `${colour}: ${s.crossingsBad} single-line crossing${s.crossingsBad === 1 ? '' : 's'} no loop can absorb`, fixes: [] })
  return out
}

/** The month's issues in one line each, for the log and the screen. */
export const issueLines = (issues: MonthIssue[]): string[] => issues.map((i) => i.text)

/** One day-type's demand, as the caller resolves it (law 6 lives there). */
export type DemandLookup = (dayClass: string) => { cls: ClassDemand; un: number; basis: string } | null

/** The railway: what it costs, what it owns, and the planner's barriers. */
export interface BuildEnv {
  mode: StudioMode
  /** The unconstrained network — the path budget is applied per colour. */
  net0: Network
  /** The planner's engine parameters (tolerance, recapture, dwell, returns). */
  params: EngineParams
  /** The "as things stand" parameters used to score TODAY's timetable. */
  basePr: EngineParams
  rates: CostModel
  fleet: FleetCaps
  sets: CarriageSet[]
  dieselLoco: number
  objective: 'profit'
  farePct: number
  minRevPerDep: number
  maxUnreturned: number
  observedPaths: number
  firstTrain: string
  lastTrain: string
  classLabels: Record<string, string>
  monthNames: string[]
  /** Which optimiser builds each month: 'claude' = the greedy Best fit (default),
   *  'ilp' = the Advanced ILP engine. Both re-score on identical machinery. */
  engineMode?: 'claude' | 'ilp'
  /** Empty-stock positioning moves run diesel (light-engine) — on by default. */
  ecsDiesel?: boolean
}

/** A month's real working timetable, as fetched from thebigplan. */
export interface MonthWtt {
  workings: BaselineWorking[]
  patternCodes: string[]
  timetableName?: string
  effectiveFrom?: string
  effectiveTo?: string
  days?: CalendarDay[]
}

/** What scoring one timetable across a colour's running days produced. */
export interface ColourScore {
  applied: Departure[]
  cost: ReturnType<typeof costFromDeps>
  dl: number
  svc: number
  setsInTraffic: number
  byDep?: Record<string, string>
  byClass: { dayClass: string; days: number; carriedD: number; revenueD: number; unreturned: number; oneWayD: number; lostD: number; reason?: string }[]
  /** Inside the Brief on every rule: everyone home (within the allowance),
   *  within the fleet, inside the path budget, stock stabled, crossings absorbed. */
  clean: boolean
  days: number
  carriedD: number
  revenueD: number
  costD: number
  contribD: number
  /** Passengers a day without a seat home on the busiest day-type. */
  unreturned: number
  /** Passengers a day carried over as one-way, as on today's timetable. */
  oneWayD: number
  /** Passengers a day TURNED AWAY BY TIMING (Chris, 2026-09-07): people in the
   *  day's demand who did not travel — no train within the tolerance of their
   *  time once the Recapture discount applies, or the nearest train had no way
   *  home (engine.ts Scenario.lostSales). Day-weighted over the colour's days. */
  lostD: number
  /** Steam engines the timetable WANTS before the fleet rule (law 4) runs the
   *  overflow diesel — shown against the Brief's fleet, never hidden. */
  steamWanted: number
  pathsOver: number
  stockOut: number
  crossingsBad: number
}

/** Everything a built month yields. `skipped` is non-null when nothing was. */
export interface MonthBuild {
  monthKey: string
  runDays: number
  dayPlans: SeasonDayPlan[]
  mod: YearSideSnap | null
  cur: YearSideSnap | null
  contributionPerDay: number
  carried: number
  unreturned: number
  services: number
  clean: boolean
  /** Where the Brief bound, in words, with the Brief changes that would settle
   *  each point (colourIssues) — empty when the month is inside the Brief. */
  issues: MonthIssue[]
  headlineDeps: Departure[]
  basis: string
  /** Every line the planner should see, in order. */
  log: string[]
  /**
   * The month could not be ATTEMPTED — no running days, no baseline, no sales.
   * Nothing is saved for it.
   */
  skipped: string | null
  /**
   * The month WAS modelled and the honest answer is that no train pays.
   *
   * This is a finding, not a failure, and it is emphatically not the same thing
   * as `skipped` — the month still has a modelled side (nought turns, nought
   * cost) and still gets saved, so the screen shows the answer instead of
   * falling back to the real timetable and reading as never modelled (PR #225).
   * Law 8: an empty result is an ANSWER, so it is named.
   */
  emptyReason: string | null
}

const gbp = (v: number) => `£${Math.round(v).toLocaleString('en-GB')}`

// ---------------------------------------------------------------------------
//  What demand a colour's plan sees
// ---------------------------------------------------------------------------

/**
 * THE ONE OWNER of "what demand does this colour's plan see".
 *
 * The design gets one blended day of the whole week that colour runs; the
 * valuation gets every day-type separately, on its own sales. `demandFor` is
 * injected so that WHICH SEASON's sales a month uses (law 6, and the declared
 * Bridge 42 exception) stays with the caller, where it is separately gated.
 *
 * A plan banked before 2026-08-25 has no `spans` and falls back to its own
 * single `dayClass`, so old slices read back unchanged.
 */
export function planDemand(
  ref: { dayClass: string; dates: string[]; spans?: DayClassSpan[] },
  demandFor: DemandLookup,
  classLabels: Record<string, string> = {},
  /**
   * TODAY's timetable for this colour. When given, each day-type is run
   * through it first and the passengers it cannot bring home are marked
   * one-way (engine.ts::oneWayShares) — they keep their sale in every design
   * and are never counted stranded (Chris, 2026-09-04). The blended design day
   * inherits the shares passenger-weighted (blendParts).
   */
  today?: TodayTimetable,
): GroupDemand | null {
  const spans = ref.spans && ref.spans.length > 0 ? ref.spans : [{ dayClass: ref.dayClass, dates: ref.dates }]
  const parts: DemandPart[] = []
  const bases: string[] = []
  const oneWayToday: Record<string, Record<string, number>> = {}
  for (const sp of spans) {
    if (sp.dates.length === 0) continue
    const md = demandFor(sp.dayClass)
    if (!md) continue
    let cls = md.cls
    if (today) {
      const shares = oneWayShares(today.net, today.deps, cls, today.params)
      if (Object.keys(shares).length > 0) {
        oneWayToday[sp.dayClass] = shares
        cls = stampOneWay(cls, shares)
      }
    }
    parts.push({ dayClass: sp.dayClass, days: sp.dates.length, cls, un: md.un })
    if (!bases.includes(md.basis)) bases.push(md.basis)
  }
  if (parts.length === 0) return null
  const days = parts.reduce((t, p) => t + p.days, 0)
  const mix = parts.map((p) => `${classLabels[p.dayClass] ?? p.dayClass} x${p.days}`).join(' · ')
  const basis = bases.join('; ')
  const ow = today ? { oneWayToday } : {}
  if (parts.length === 1) return { design: { cls: parts[0].cls, un: parts[0].un, basis }, parts, mix, days, ...ow }
  const b = blendParts(parts)
  return {
    design: { cls: b.cls, un: b.un, basis: `${basis} — one timetable across the whole week (${days} days: ${mix})` },
    parts, mix, days, ...ow,
  }
}

/**
 * Re-stamp a colour's demand with the one-way-today shares BANKED with its plan
 * (SeasonDayPlan.oneWayToday), so a banked design is valued against exactly the
 * people the build saw — the screen, the workbook and the board paper never
 * recompute who is one-way by a second route.
 */
export function applyOneWayToday(gd: GroupDemand, byClass: Record<string, Record<string, number>> | undefined): GroupDemand {
  if (!byClass) return gd
  const parts = gd.parts.map((p) => ({ ...p, cls: stampOneWay(p.cls, byClass[p.dayClass]) }))
  if (parts.length === 1) return { ...gd, parts, design: { ...gd.design, cls: parts[0].cls }, oneWayToday: byClass }
  const b = blendParts(parts)
  return { ...gd, parts, design: { ...gd.design, cls: b.cls, un: b.un }, oneWayToday: byClass }
}

// ---------------------------------------------------------------------------
//  What one timetable is worth across a colour's days
// ---------------------------------------------------------------------------

/**
 * THE ONE OWNER of "what is this timetable worth across a colour's days".
 *
 * The set plan, the diagrams and the cost belong to the TIMETABLE — they do not
 * change with the day of the week. The demand side is run separately for every
 * day-type on its own sales and day-weighted (law 6). The fleet overflow rule
 * is applied here and ONLY here, so the interactive run, the season build and a
 * pattern edit can never disagree about how many steam engines a plan needs.
 *
 * `pre` is passed when the caller has ALREADY planned sets — Best fit does its
 * own — so the plan is never re-made behind its back.
 */
export function scoreColourPlan(args: {
  net: Network
  deps: Departure[]
  gd: GroupDemand
  pr: EngineParams
  fareMult: number
  dl0: number
  formation: SetFormation
  env: Pick<BuildEnv, 'rates' | 'fleet' | 'sets' | 'objective'> & Partial<Pick<BuildEnv, 'maxUnreturned'>>
  pre?: { carriages: number; setsInTraffic: number; byDep?: Record<string, string> }
}): ColourScore {
  const { net: n, deps, gd, pr, fareMult, dl0, formation, env, pre } = args
  let applied = deps
  let carriages = pre?.carriages
  let setsInTraffic = pre?.setsInTraffic ?? 0
  let byDep = pre?.byDep
  if (!pre) {
    const plan = planSets(n, [gd.design.cls], deps, env.sets, pr, env.objective, env.rates, dl0, env.fleet, Number.MAX_SAFE_INTEGER, formation)
    applied = applySetPlan(deps, plan)
    carriages = plan.carriages
    setsInTraffic = plan.setsInTraffic
    byDep = plan.byDep
  }
  let dl = dl0
  let cost = costFromDeps(n, applied, env.rates, dl, env.fleet, carriages, setsInTraffic)
  const steamWanted = cost.steamN
  // Law 4 — fleet is physics. A design needing a fourth engine when only three
  // steam exist runs the overflow diagrams DIESEL, never a phantom steam
  // engine. Beyond the diesel fleet too, overCap still disqualifies it.
  if (cost.steamN > env.fleet.maxSteam) {
    dl = dl0 + (cost.steamN - env.fleet.maxSteam)
    cost = costFromDeps(n, applied, env.rates, dl, env.fleet, carriages, setsInTraffic)
  }
  const svc = applied.filter((x) => x.enabled && !x.positioning).length
  let carried = 0, revenue = 0, unreturned = 0, oneWay = 0, lost = 0
  const byClass: ColourScore['byClass'] = []
  for (const pt of gd.parts) {
    const sc = runClasses(n, [pt.cls], applied, pr)
    // D4 guard: no services means no walk-up uplift.
    const revD = sc.revenue + (svc > 0 ? pt.un * fareMult : 0)
    carried += sc.carried * pt.days
    revenue += revD * pt.days
    unreturned = Math.max(unreturned, sc.unreturned)
    const owD = sc.oneWay.filter((x) => x.asToday).reduce((t, x) => t + x.count, 0)
    oneWay += owD * pt.days
    lost += sc.lostSales * pt.days
    const top = [...sc.stranded].sort((a, b) => b.count - a.count)[0]
    byClass.push({
      dayClass: pt.dayClass, days: pt.days, carriedD: sc.carried, revenueD: revD, unreturned: sc.unreturned, oneWayD: owD, lostD: sc.lostSales,
      reason: top ? `${Math.round(top.count)} went out at ${top.time} and ${top.reason}` : undefined,
    })
  }
  const D = gd.days || 1
  const pathsOver = pathUsage(n, applied).reduce((t, x) => t + (x.over ? Math.max(1, x.used - x.available) : 0), 0)
  const stockOut = stockImbalances(n, applied).reduce((t, i) => t + i.count, 0)
  const crossingsBad = crossingConflicts(n, applied).filter((x) => !x.resolvable).length
  // A colour that runs NOTHING is an answer (no working clears the floor), not
  // an unfinished build (Chris, 2026-09-04: "the line costs money and runs all
  // year round; it is merely the standard services you are modelling"): it is
  // clean, and its month carries its fixed cost with no trains against it.
  // Everyone-home is judged against the Brief's allowance, passengers a day on
  // the busiest day-type — the slider is the planner's tolerance, not decoration.
  const allowance = Math.max(0.5, env.maxUnreturned ?? 0)
  // Locomotives may not appear or vanish (Chris, 2026-09-07): an engine that
  // would begin or end its day at a station with no shed puts the month outside
  // the Brief — reported, never a silent pass.
  const clean = unreturned <= allowance && cost.overCap.length === 0 && cost.locoOut.length === 0
    && pathsOver === 0 && stockOut === 0 && crossingsBad === 0
  return {
    applied, cost, dl, svc, setsInTraffic, byDep, byClass, clean,
    days: D,
    carriedD: carried / D,
    revenueD: revenue / D,
    costD: cost.total,
    contribD: revenue / D - cost.total,
    unreturned,
    oneWayD: oneWay / D,
    lostD: lost / D,
    steamWanted,
    pathsOver, stockOut, crossingsBad,
  }
}

// ---------------------------------------------------------------------------
//  The colour's own baseline: window and path budget
// ---------------------------------------------------------------------------

/**
 * One colour's real workings give the design its operating window and its
 * Network Rail path budget. Law 3: paths are TODAY'S usage, physics — the
 * design never plans for more than the railway already runs.
 *
 * Returns null when the fetched timetable publishes no pattern for this colour.
 * That is an ANSWER, and the caller states it: it is how March 2026 lost seven
 * Yellow days in silence.
 */
export function colourBaseline(args: {
  colour: string
  workings: BaselineWorking[]
  formation: SetFormation
  env: Pick<BuildEnv, 'mode' | 'net0' | 'observedPaths' | 'firstTrain' | 'lastTrain'>
  /**
   * The demand this baseline is supposed to serve. Given it, the baseline is
   * checked for REACH — see `unreachable` below. Omit to skip the check.
   */
  demand?: ClassDemand
}): {
  deps: Departure[]
  firstMin: number
  lastMin: number
  net: Network
  observedPaths: number
  /**
   * Stations the DEMAND wants but this published pattern never reaches, with
   * the passengers involved.
   *
   * Chris, 2026-08-25, on next year's draft timetable: *"nobody has even thought
   * about 2027... get this wrong and we'll bankrupt the railway."*
   * `tt-ss27-yellow` is a byte-for-byte copy of this year's Yellow — the Bridge
   * 42 Pickering-Goathland shuttle — and 22 of 2027's running days are Yellow.
   * A pattern that stops at Goathland supplies a Goathland operating window and
   * a zero Network Rail path budget, so it would silently cap a Whitby design to
   * a severed railway, with every internal figure agreeing. A wrong draft
   * timetable must not be able to do that quietly, so it is REPORTED and the
   * window falls back to what the demand itself evidences.
   */
  unreachable: { station: number; paxPerDay: number }[]
} | null {
  const { colour, workings, formation, env, demand } = args
  const rows = workings.filter((x) => x.pattern_code === `TT-${colour.toUpperCase()}`)
  if (rows.length === 0) return null
  const deps = wttToDeps(rows, formation).deps

  // Which stations does the published pattern actually serve?
  const served = new Set<number>()
  for (const d of deps) { served.add(d.o); served.add(d.d) }
  const unreachable: { station: number; paxPerDay: number }[] = []
  if (demand) {
    const want = new Map<number, number>()
    for (const sl of [...demand.north, ...demand.south]) {
      for (const st of [sl.o, sl.d]) {
        if (!served.has(st)) want.set(st, (want.get(st) ?? 0) + sl.paxPerDay)
      }
    }
    for (const [station, paxPerDay] of [...want.entries()].sort((a, b) => b[1] - a[1])) {
      if (paxPerDay > 0.5) unreachable.push({ station, paxPerDay })
    }
  }

  // The window comes from the pattern — UNLESS the pattern demonstrably
  // describes a different railway from the one the demand is for, in which case
  // the demand's own extent is the honest evidence (law 8: say it, don't cap it
  // silently). The caller logs `unreachable`.
  const demandMins = demand ? [...demand.north, ...demand.south].map((x) => x.minutes) : []
  const useDemandWindow = unreachable.length > 0 && demandMins.length > 0
  const firstMin = useDemandWindow
    ? Math.min(...demandMins)
    : (deps.length ? Math.min(...deps.map((d) => d.minutes)) : toMin(env.firstTrain) || 0)
  const lastMin = useDemandWindow
    ? Math.max(Math.max(...demandMins) + 120, deps.length ? Math.max(...deps.map((d) => d.arrMin ?? d.minutes)) : 0)
    : (deps.length ? Math.max(...deps.map((d) => d.arrMin ?? d.minutes)) : toMin(env.lastTrain) || 24 * 60)

  // Paths stay physics (law 3). A shuttle pattern observes no Network Rail
  // traversals at all, so the session's observed count stands in rather than
  // zero — but it is never RAISED above what the railway already runs.
  const obs = observedNrTraversals(env.net0, deps)
  const net = withPathBudget(buildNetwork(env.mode, {}), obs > 0 ? obs : env.observedPaths)
  return { deps, firstMin, lastMin, net, observedPaths: obs, unreachable }
}

// ---------------------------------------------------------------------------
//  Both sides of a month
// ---------------------------------------------------------------------------

export type { YearSideSnap }

/** Day-weight a per-engine map (miles, or 1 = worked) across colour scores. */
function foldEngine(rows: ColourScore[], pick: (r: ColourScore) => Record<string, number>, D: number): Record<string, number> | undefined {
  const acc: Record<string, number> = {}
  let any = false
  for (const r of rows) {
    for (const [k, v] of Object.entries(pick(r))) { acc[k] = (acc[k] ?? 0) + v * r.days; any = true }
  }
  if (!any) return undefined
  for (const k of Object.keys(acc)) acc[k] = acc[k] / D
  return acc
}

/** Day-weight a set of colour scores into one side of a month. Exported for the
 *  home gate, which proves the turned-away figure folds as passengers do. */
export function foldSide(scores: { s: ColourScore }[]): YearSideSnap | null {
  const rows = scores.map((x) => x.s)
  const D = rows.reduce((t, r) => t + r.days, 0)
  if (D === 0) return null
  // Named engines (Chris, 2026-09-11): each register engine's timetabled miles
  // per running day and the share of days it works — the hire lines' drivers.
  const engineMilesPerDay = foldEngine(rows, (r) => r.cost.engineMiles ?? {}, D)
  const engineDaysPerDay = foldEngine(rows, (r) => Object.fromEntries((r.cost.engineWorked ?? []).map((k) => [k, 1])), D)
  const svcMin = Math.min(...rows.map((r) => r.svc))
  const svcMax = Math.max(...rows.map((r) => r.svc))
  return {
    services: Math.round(rows.reduce((t, r) => t + r.svc * r.days, 0) / D),
    servicesRange: svcMin !== svcMax ? [svcMin, svcMax] : undefined,
    steamN: Math.max(...rows.map((r) => r.cost.steamN)),
    dieselN: Math.max(...rows.map((r) => r.cost.dieselN + r.cost.railcars)),
    carriedPerDay: rows.reduce((t, r) => t + r.carriedD * r.days, 0) / D,
    revenuePerDay: rows.reduce((t, r) => t + r.revenueD * r.days, 0) / D,
    costsPerDay: rows.reduce((t, r) => t + r.costD * r.days, 0) / D,
    // REAL train-miles per running day, from the engine's per-segment mileage
    // (Chris, 2026-09-02: a Grosmont–Whitby turn is 6 miles, not a 24-mile
    // full-line proxy). The P&L cost model prices train-mile lines with this.
    milesPerDay: rows.reduce((t, r) => t + (r.cost.steamMiles + r.cost.dieselMiles) * r.days, 0) / D,
    // Turned away by timing, per running day (Chris, 2026-09-07) — the one
    // figure the season table, the workbook Summary, Financial Impact and
    // the board paper's footnote all read through monthFigures.
    turnedAwayPerDay: rows.reduce((t, r) => t + r.lostD * r.days, 0) / D,
    ...(engineMilesPerDay ? { engineMilesPerDay } : {}),
    ...(engineDaysPerDay ? { engineDaysPerDay } : {}),
    scoredDays: D,
  }
}

/**
 * Score a month's CURRENT side — the real working timetable of each colour it
 * runs, valued day-type by day-type exactly as the proposal is. The same owner
 * values both sides, so the two can never be built by different methods and
 * the delta can never be an artefact of the arithmetic.
 */
export function scoreMonthCurrent(args: {
  groups: SeasonDayGroup[]
  wtt: MonthWtt
  formation: SetFormation
  demandFor: DemandLookup
  env: BuildEnv
}): YearSideSnap | null {
  const { groups, wtt, formation, demandFor, env } = args
  const scores: { s: ColourScore }[] = []
  for (const g of groups) {
    if (unscorableColour(g.colour)) continue
    // No `demand` here on purpose: this scores what the railway ACTUALLY runs
    // today, so its own window is the truth even if it reaches nowhere.
    const base = colourBaseline({ colour: g.colour, workings: wtt.workings, formation, env })
    if (!base || base.deps.length === 0) continue
    // Today's own one-way travellers are one-way here too — so today's side
    // reads 0 stranded for the people the railway already sells that journey.
    const gd = planDemand(g, demandFor, env.classLabels, { net: env.net0, deps: base.deps, params: env.basePr })
    if (!gd) continue
    scores.push({
      s: scoreColourPlan({
        net: env.net0, deps: base.deps, gd, pr: env.basePr, fareMult: 1,
        dl0: env.dieselLoco, formation, env,
      }),
    })
  }
  return foldSide(scores)
}

/**
 * BUILD ONE MONTH.
 *
 * One Best fit per timetable colour, built against the whole week that colour
 * runs, then valued day-type by day-type. Pure and synchronous: same inputs,
 * same answer, every time — which is what makes it gate-able.
 */
export function buildMonth(args: {
  monthKey: string
  groups: SeasonDayGroup[]
  runDays: number
  wtt: MonthWtt
  formation: SetFormation
  demandFor: DemandLookup
  env: BuildEnv
}): MonthBuild {
  const { monthKey, groups, runDays, wtt, formation, demandFor, env } = args
  const mm = Number(monthKey.slice(5, 7))
  const mName = env.monthNames[mm - 1] ?? monthKey
  const log: string[] = []
  const empty = (why: string): MonthBuild => ({
    monthKey, runDays, dayPlans: [], mod: null,
    cur: scoreMonthCurrent({ groups, wtt, formation, demandFor, env }),
    contributionPerDay: 0, carried: 0, unreturned: 0, services: 0,
    clean: false, issues: [], headlineDeps: [], basis: '', log, skipped: why, emptyReason: null,
  })

  if (runDays === 0) return empty('no running days in the calendar')

  // LOUD, never silent (law 8). March 2026 ran seven Yellow days against a
  // timetable with no Yellow pattern, so those days had no baseline and the
  // month produced nothing — for three sessions, written up as a profit-floor
  // finding. Whatever else happens now, the planner is told.
  const straddle = straddleWarning({
    monthKey,
    effectiveFrom: wtt.effectiveFrom,
    effectiveTo: wtt.effectiveTo,
    runningDates: groups.flatMap((g) => g.dates),
  })
  if (straddle) log.push(`  ! ${mName}: ${straddle}.`)
  for (const m of coloursWithoutPattern({ groups, patternCodes: wtt.patternCodes })) {
    log.push(`  ! ${mName} · ${m.colour} x${m.days} day${m.days === 1 ? '' : 's'}: the ${wtt.timetableName ?? 'fetched'} timetable publishes no ${m.colour} pattern, so these days have NO baseline — no operating window, no path budget, nothing to compare against. They cannot be modelled until thebigplan publishes a ${m.colour} timetable covering them.`)
  }

  const ordered = [...groups].sort((a, b) => b.dates.length - a.dates.length)
  const dayPlans: SeasonDayPlan[] = []
  const scores: { s: ColourScore }[] = []
  const issues: MonthIssue[] = []
  let basis = ''

  for (const g of ordered) {
    if (unscorableColour(g.colour)) {
      log.push(`  ${mName} · ${g.colour || 'uncoloured'} x${g.dates.length} day${g.dates.length === 1 ? '' : 's'}: no standard pattern to redesign (gala/diner) — left as is.`)
      continue
    }
    const gd = planDemand(g, demandFor, env.classLabels)
    if (!gd) {
      log.push(`  ${mName} · ${g.colour} x${g.dates.length}: skipped — no sales data for these days.`)
      continue
    }
    basis = gd.design.basis
    const base = colourBaseline({ colour: g.colour, workings: wtt.workings, formation, env, demand: gd.design.cls })
    if (!base) {
      log.push(`  ${mName} · ${g.colour} x${g.dates.length}: skipped — no ${g.colour} workings in ${wtt.timetableName ?? 'the fetched timetable'} (an empty answer, not a design).`)
      continue
    }
    if (base.unreachable.length > 0) {
      const names = base.unreachable
        .map((u) => `${env.net0.stations[u.station]?.name ?? u.station} (${Math.round(u.paxPerDay)} passengers a day)`)
        .join(', ')
      log.push(`  ! ${mName} · ${g.colour}: the published ${g.colour} pattern in ${wtt.timetableName ?? 'the fetched timetable'} never reaches ${names}, but the sales for these days do. That pattern describes a different railway, so the operating window is taken from the sales instead of from it. Check the ${g.colour} timetable — this is what a stale or disrupted draft looks like.`)
    }
    const mParams: EngineParams = { ...env.params, opts: { ...env.params.opts, lastMin: base.lastMin } }
    // Who is one-way TODAY, on this colour's real timetable: those passengers
    // keep their sale and are never stranded, in the build and in every
    // valuation of it (Chris, 2026-09-04). Same demand as `gd`, stamped.
    const gdT = planDemand(g, demandFor, env.classLabels, { net: base.net, deps: base.deps, params: mParams }) ?? gd
    const uniq = (pick: (c: ClassDemand) => Slot[]) =>
      new Set(pick(gdT.design.cls).map((s) => `${s.time}|${s.route}`)).size
    // ONE optimiser run for the colour, over the whole week it runs — the greedy
    // Best fit by default, or the Advanced ILP engine when the planner selects it.
    // Designed against the blended week; everyone-home judged on EVERY day-type.
    const fitCfg = {
      net: base.net, classes: [gdT.design.cls], params: mParams, minGainPerDep: env.minRevPerDep,
      checkClasses: gdT.parts.map((p) => ({ ...p.cls, weight: 1 })),
      maxPerDir: Math.min(10, Math.max(uniq((c) => c.north), uniq((c) => c.south)) + 3),
      objective: env.objective, rates: env.rates, dieselLoco: env.dieselLoco, fleet: env.fleet,
      sets: env.sets, maxUnreturned: env.maxUnreturned,
      firstMin: base.firstMin, lastMin: base.lastMin, formation,
      ecsDiesel: env.ecsDiesel,
    }
    const r = env.engineMode === 'ilp' ? ilpBuild(fitCfg) : bestFit(fitCfg)
    const s = scoreColourPlan({
      net: base.net, deps: r.deps, gd: gdT, pr: mParams, fareMult: env.farePct / 100,
      dl0: env.dieselLoco, formation, env,
      pre: { carriages: r.plan.carriages, setsInTraffic: r.plan.setsInTraffic, byDep: r.plan.byDep },
    })
    dayPlans.push({ ...g, deps: r.deps, dl: s.dl, oneWayToday: gdT.oneWayToday })
    scores.push({ s })
    const colourIss = colourIssues(s, g.colour, env)
    issues.push(...colourIss)
    log.push(`  ${mName} · ${g.colour} x${s.days} day${s.days === 1 ? '' : 's'} (${gdT.mix}): ${s.svc} turns/day · ${s.cost.steamN}S${s.cost.dieselN + s.cost.railcars > 0 ? `+${s.cost.dieselN + s.cost.railcars}D` : ''} · contribution ${gbp(s.contribD)}/day${s.oneWayD >= 0.5 ? ` · ${Math.round(s.oneWayD)} a day travel one way, as today` : ''}${s.lostD >= 0.5 ? ` · ${Math.round(s.lostD)} a day turned away by timing` : ''}${s.steamWanted > env.fleet.maxSteam ? ` · wants ${s.steamWanted} steam engines to keep every diagram inside footplate hours; the Scope allows ${env.fleet.maxSteam}, so ${s.steamWanted - env.fleet.maxSteam} run${s.steamWanted - env.fleet.maxSteam === 1 ? 's' : ''} diesel` : ''}${s.clean ? '' : ' — ! outside the Scope'}`)
    for (const l of r.log.filter((x) => x.startsWith('⚠')).slice(0, 4)) log.push(`    ${l}`)
    for (const i of colourIss) log.push(`    ! ${i.text}`)
  }

  if (scores.length === 0) return empty('no colour of this month could be designed — see the lines above')

  const mod = foldSide(scores)!
  const rows = scores.map((x) => x.s)
  const D = rows.reduce((t, r) => t + r.days, 0)
  // The headline timetable is the largest colour that ACTUALLY BUILT something.
  // Taking the largest blindly banked an EMPTY headline, so the month fell back
  // to the real WTT on screen and read as never modelled (PR #225).
  const headline = dayPlans.find((p) => p.deps.length > 0) ?? dayPlans[0]
  // EVERY timetable of the month built nothing. That is an answer — the floor
  // is a judgement the planner can change — and it must be said, not shown as
  // a design with no trains in it.
  const anyTurns = dayPlans.some((p) => p.deps.filter((x) => x.enabled && !x.positioning).length > 0)
  const emptyReason = anyTurns ? null
    : `every timetable this month was modelled, and no working cleared the ${gbp(env.minRevPerDep)} minimum profit per working on ${basis}. That is a finding about the month, not a failure of the build — and the floor is a judgement you can change.`
  if (emptyReason) log.push(`  ! ${mName}: ${emptyReason}`)
  return {
    monthKey, runDays, dayPlans, mod,
    cur: scoreMonthCurrent({ groups, wtt, formation, demandFor, env }),
    contributionPerDay: rows.reduce((t, r) => t + r.contribD * r.days, 0) / D,
    carried: rows.reduce((t, r) => t + r.carriedD * r.days, 0),
    unreturned: Math.max(...rows.map((r) => r.unreturned)),
    services: mod.services,
    clean: rows.every((r) => r.clean),
    issues,
    headlineDeps: headline?.deps ?? [],
    basis,
    log: [
      ...log,
      `${mName} (${basis}): ${scores.length} timetable${scores.length === 1 ? '' : 's'} — one per colour — over ${D} of ${runDays} running days · contribution ${gbp(rows.reduce((t, r) => t + r.contribD * r.days, 0) / D)}/day${issues.length === 0 ? ' · inside the Scope' : ` — ! outside the Scope on ${issues.length} point${issues.length === 1 ? '' : 's'} (listed above; ⚠ Interventions offers the fixes)`}`,
    ],
    skipped: null,
    emptyReason,
  }
}

/**
 * Re-score a month's banked plans AS THEY STAND — no Best fit, because the
 * planner's edit IS the timetable. Same owners, same arithmetic, so editing a
 * pattern can never move the money by a different route than building it did.
 */
export function rescoreMonth(args: {
  monthKey: string
  dayPlans: SeasonDayPlan[]
  groups: SeasonDayGroup[]
  runDays: number
  wtt: MonthWtt
  formation: SetFormation
  demandFor: DemandLookup
  env: BuildEnv
}): MonthBuild {
  const { monthKey, dayPlans, groups, runDays, wtt, formation, demandFor, env } = args
  const mm = Number(monthKey.slice(5, 7))
  const mName = env.monthNames[mm - 1] ?? monthKey
  const log: string[] = []
  const scores: { s: ColourScore }[] = []
  let basis = ''
  const issues: MonthIssue[] = []
  for (const p of dayPlans) {
    if (p.deps.filter((x) => x.enabled).length === 0) continue
    const base = colourBaseline({ colour: p.colour, workings: wtt.workings, formation, env })
    const net = base?.net ?? withPathBudget(buildNetwork(env.mode, {}), env.observedPaths)
    const pr: EngineParams = base ? { ...env.params, opts: { ...env.params.opts, lastMin: base.lastMin } } : env.params
    const gd = planDemand(p, demandFor, env.classLabels, base ? { net: base.net, deps: base.deps, params: pr } : undefined)
    if (!gd) continue
    basis = gd.design.basis
    const s = scoreColourPlan({
      net, deps: p.deps, gd, pr, fareMult: env.farePct / 100,
      dl0: p.dl ?? env.dieselLoco, formation, env,
    })
    p.dl = s.dl
    p.oneWayToday = gd.oneWayToday
    scores.push({ s })
    issues.push(...colourIssues(s, p.colour, env))
  }
  if (scores.length === 0) {
    return {
      monthKey, runDays, dayPlans, mod: null,
      cur: scoreMonthCurrent({ groups, wtt, formation, demandFor, env }),
      contributionPerDay: 0, carried: 0, unreturned: 0, services: 0,
      clean: false, issues: [], headlineDeps: [], basis, log,
      skipped: 'no banked plan could be scored', emptyReason: null,
    }
  }
  const mod = foldSide(scores)!
  const rows = scores.map((x) => x.s)
  const D = rows.reduce((t, r) => t + r.days, 0)
  const headline = [...dayPlans].sort((a, b) => b.dates.length - a.dates.length).find((p) => p.deps.length > 0) ?? dayPlans[0]
  log.push(`${mName}: re-scored ${scores.length} timetable${scores.length === 1 ? '' : 's'} as edited · contribution ${gbp(rows.reduce((t, r) => t + r.contribD * r.days, 0) / D)}/day`)
  return {
    monthKey, runDays, dayPlans, mod,
    cur: scoreMonthCurrent({ groups, wtt, formation, demandFor, env }),
    contributionPerDay: rows.reduce((t, r) => t + r.contribD * r.days, 0) / D,
    carried: rows.reduce((t, r) => t + r.carriedD * r.days, 0),
    unreturned: Math.max(...rows.map((r) => r.unreturned)),
    services: mod.services,
    clean: rows.every((r) => r.clean),
    issues,
    headlineDeps: headline?.deps ?? [],
    basis, log, skipped: null,
    emptyReason: dayPlans.some((p) => p.deps.filter((x) => x.enabled && !x.positioning).length > 0)
      ? null
      : `no working in this month's edited timetables clears the ${gbp(env.minRevPerDep)} minimum profit per working.`,
  }
}
